import {
  CheckboxWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { Box, Button, MenuItem, Typography } from '@material-ui/core'
import { useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import { t } from './shared'
import useStyles from './styles/ProviderConfigForm.styles'
import { ResourceRow } from './types'

const CHANNELS = ['SMS', 'EMAIL'] as const

/**
 * The settings keys the service accepts, per channel.
 *
 * `normalizeOperationalSettings` rejects the whole request on any key outside
 * this set, so offering a free-form JSON box here would let an operator submit
 * something the service refuses with no explanation on this screen. `endpoint`
 * is common to both channels.
 */
const SETTING_FIELDS: Record<string, { key: string; label: string }[]> = {
  SMS: [{ key: 'sender_number', label: 'Sender number' }],
  EMAIL: [
    { key: 'from_address', label: 'From address' },
    { key: 'reply_to', label: 'Reply-to' }
  ]
}

/**
 * The credential fields each channel's provider expects.
 *
 * These are the JSON keys the service unmarshals — `api_key` for the SMS
 * gateway, `username`/`password` for SMTP. They are named here rather than
 * left to a JSON box because a mistyped key produces a provider rejection at
 * save time rather than a field error.
 */
const CREDENTIAL_FIELDS: Record<
  string,
  { key: string; label: string; password?: boolean }[]
> = {
  SMS: [{ key: 'api_key', label: 'API key', password: true }],
  EMAIL: [
    { key: 'username', label: 'Username' },
    { key: 'password', label: 'Password', password: true }
  ]
}

interface ProviderConfigFormProps {
  initial?: ResourceRow
  saving: boolean
  onSubmit: (config: ResourceRow) => void
}

/**
 * A real form for provider configurations, replacing the JSON textarea.
 *
 * This resource is the one that could not simply be made editable. The list
 * returns a `credentialFingerprint`, never the credentials, so a form that
 * round-tripped a row would submit an empty credentials object — and the
 * service used to encrypt that over the stored secret. It now treats an
 * omitted object on an update as "leave what is stored", which is what makes
 * this form possible.
 *
 * The service also refuses to preserve credentials and change the endpoint in
 * one request: verification sends the credentials to that endpoint, so
 * redirecting it without proving you know the secret would turn an edit
 * permission into a read permission. This form makes that rule visible rather
 * than letting it arrive as a save error — the endpoint is read-only until the
 * operator chooses to replace the credentials.
 */
export default function ProviderConfigForm({
  initial,
  saving,
  onSubmit
}: ProviderConfigFormProps) {
  const classes = useStyles()
  const creating = !initial

  const [draft, setDraft] = useState<ResourceRow>(
    initial ?? {
      id: '',
      channel: 'SMS',
      providerKey: '',
      settings: {},
      active: true
    }
  )
  // Forced on when creating: there is nothing stored to leave.
  const [replacing, setReplacing] = useState(creating)
  const [credentials, setCredentials] = useState<Record<string, string>>({})

  const set = (field: string, value: unknown) =>
    setDraft((current) => ({ ...current, [field]: value }))

  /**
   * Switching channel rewrites both channel-shaped pieces of the draft.
   *
   * Settings and credentials are keyed differently per channel, and neither is
   * additive: `normalizeOperationalSettings` rejects the WHOLE request on any
   * key outside the new channel's set, so an SMS `sender_number` left behind
   * on an EMAIL config makes the form unsaveable with an error that names
   * settings generally rather than the stale key. `endpoint` is common to both
   * and is kept.
   *
   * Credentials are dropped rather than pruned: an `api_key` typed for the SMS
   * gateway is not a partial SMTP credential, and storing it alongside one
   * would persist a secret for a provider this configuration no longer uses.
   */
  const setChannel = (next: string) => {
    setDraft((current) => {
      const currentSettings = (current.settings ?? {}) as Record<string, string>
      const kept: Record<string, string> = {}
      if (currentSettings.endpoint) kept.endpoint = currentSettings.endpoint
      for (const field of SETTING_FIELDS[next] ?? []) {
        if (currentSettings[field.key])
          kept[field.key] = currentSettings[field.key]
      }
      return { ...current, channel: next, settings: kept }
    })
    setCredentials({})
  }

  const channel = String(draft.channel ?? 'SMS')
  const settings = (draft.settings ?? {}) as Record<string, string>
  const setSetting = (key: string, value: string) =>
    set('settings', { ...settings, [key]: value })

  const endpointLocked = !replacing

  return (
    <Box mt={2}>
      <SectionTitle title={t('Create or update provider configuration')} />

      <div className={classes.grid}>
        <InputWithLabel
          id="provider-id"
          name="provider-id"
          label={t('Identifier')}
          value={String(draft.id ?? '')}
          disabled={!creating}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('id', event.currentTarget.value)
          }
        />
        <SelectWithLabel
          id="provider-channel"
          name="provider-channel"
          label={t('Channel')}
          value={channel}
          onChange={(event: { target: { value: unknown } }) =>
            setChannel(String(event.target.value))
          }>
          {CHANNELS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(option)}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <InputWithLabel
          id="provider-key"
          name="provider-key"
          label={t('Provider key')}
          value={String(draft.providerKey ?? '')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('providerKey', event.currentTarget.value)
          }
        />
      </div>

      <div className={classes.grid}>
        <InputWithLabel
          id="provider-endpoint"
          name="provider-endpoint"
          label={t('Endpoint')}
          value={settings.endpoint ?? ''}
          disabled={endpointLocked}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSetting('endpoint', event.currentTarget.value)
          }
        />
        {(SETTING_FIELDS[channel] ?? []).map((field) => (
          <InputWithLabel
            key={field.key}
            id={`provider-${field.key}`}
            name={`provider-${field.key}`}
            label={t(field.label)}
            value={settings[field.key] ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSetting(field.key, event.currentTarget.value)
            }
          />
        ))}
      </div>

      <CheckboxWithLabel
        id="provider-active"
        name="provider-active"
        label={t('Active')}
        noHelperText
        checked={Boolean(draft.active)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          set('active', event.currentTarget.checked)
        }
      />

      <div className={classes.credentials}>
        <CheckboxWithLabel
          id="provider-replace-credentials"
          name="provider-replace-credentials"
          label={t('Replace credentials')}
          noHelperText
          checked={replacing}
          disabled={creating}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            // Read once, before the state update. The second read of
            // `currentTarget` happens after React has re-rendered and
            // unmounted the credential inputs, and it is null by then.
            const next = event.currentTarget.checked
            setReplacing(next)
            // Dropping what was typed is deliberate: leaving it staged while
            // the box is unchecked would submit a secret the operator believes
            // they abandoned.
            if (!next) setCredentials({})
          }}
        />
        {replacing ? (
          <div className={classes.grid}>
            {(CREDENTIAL_FIELDS[channel] ?? []).map((field) => (
              <InputWithLabel
                key={field.key}
                id={`provider-credential-${field.key}`}
                name={`provider-credential-${field.key}`}
                label={t(field.label)}
                type={field.password ? 'password' : 'text'}
                autoComplete="new-password"
                value={credentials[field.key] ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  // Read outside the updater. React may call a functional
                  // updater after the synthetic event has been pooled, and
                  // `currentTarget` is null by then.
                  const value = event.currentTarget.value
                  setCredentials((current) => ({
                    ...current,
                    [field.key]: value
                  }))
                }}
              />
            ))}
          </div>
        ) : (
          <Typography
            variant="caption"
            color="textSecondary"
            className={classes.hint}>
            {t(
              'The stored credentials are kept. The endpoint cannot change while they are, because saving sends them to it — tick Replace credentials to change where they go.'
            )}
          </Typography>
        )}
      </div>

      <Button
        className={classes.submit}
        variant="contained"
        color="primary"
        disabled={saving}
        onClick={() =>
          onSubmit({
            ...draft,
            // An empty object is the wire shape the service reads as "leave
            // the stored credentials alone". On a create it has nothing to
            // leave and rejects the request, which is the intended answer.
            credentials: replacing ? credentials : {}
          })
        }>
        {saving ? t('Saving…') : t('Save')}
      </Button>
    </Box>
  )
}
